import {
    ICompleteActionPostLoginModel,
    ILogger,
    IPostLoginChannel,
    ITopicSubscription,
    MessageBroker,
    PostLoginActionNameEnum,
} from '@sparkware/uc-sdk-core';
import Container from 'typedi';
import DeferredObject from '../../../../Modules/Utils/DeferredObject';
import { LoggerProvider } from '../../logger';
import { Utils } from '../../utils';
import { IPostLoginAction } from '../models/IPostLoginAction';
import { IPostLoginActionsState, PostLoginActionType } from '../models/IPostLoginActionsState';
import { PostLoginEventEmitter } from '../post-login.eventEmitter';
import { PostLoginStorage } from '../post-login.storage';
import { ClientTracking } from '../../tracking/models/clientTracking';
import { WindowToken } from '../../../injection-tokens';

export abstract class PostLoginActionBase implements IPostLoginAction {
    protected _logger: ILogger;
    protected _window: Window;
    private _actionExecutionDO: DeferredObject<void>;
    private _postLoginCompletionSubscription: ITopicSubscription;
    private _state: IPostLoginActionsState;
    private _options: PostLoginActionType;
    protected doAction: (options?: PostLoginActionType) => Promise<boolean>;

    private readonly _postLoginEventEmitter: PostLoginEventEmitter;
    private readonly _storage: PostLoginStorage;
    protected readonly _clientTracking: ClientTracking;
    protected readonly actionName: PostLoginActionNameEnum;
    protected readonly postLoginChannel: IPostLoginChannel;
    protected readonly utils: Utils;

    protected constructor(
        actionName: PostLoginActionNameEnum,
        loggerName: string,
        state: IPostLoginActionsState,
    ) {
        this._window = Container.get(WindowToken);
        this.actionName = actionName;
        this.postLoginChannel = MessageBroker.getInstance().postLogin;
        this._logger = Container.get(LoggerProvider).getLogger(loggerName);
        this.utils = Container.get(Utils);
        this._storage = Container.get(PostLoginStorage);
        this._clientTracking = Container.get(ClientTracking);
        this._state = state;
        this._options = state.actionsOptions?.[actionName];
        this._postLoginEventEmitter = Container.get(PostLoginEventEmitter);
    }

    public execute = async (): Promise<boolean> => {
        this._postLoginEventEmitter.sendActionStartEvent(
            this.actionName,
            this._state.correlationID,
        );
        let shouldEndPostLoginActions = false;
        if (await this.canExecute(this._options)) {
            const completionObjectPromise = this._createCompletionObject();

            shouldEndPostLoginActions = await this.doAction(this._options);

            await completionObjectPromise;
        } else {
            this.completeAction();
        }

        this._postLoginEventEmitter.sendActionCompleteEvent(
            this.actionName,
            this._state.correlationID,
        );
        return shouldEndPostLoginActions;
    };

    protected canExecute = (options?: PostLoginActionType): Promise<boolean> => {
        return Promise.resolve(true);
    };

    protected completeAction = () =>
        this._completeAction(
            this._postLoginCompletionSubscription,
            this._actionExecutionDO,
        )(this.actionName);

    protected clearOptions = () => {
        this._storage.updateActionOptions(this.actionName, null);
    };

    protected wasFlowInterrupted = () => this._state.interrupted;

    private _completeAction =
        (sub: ITopicSubscription, executionDO: DeferredObject<void>) =>
        (actionName: PostLoginActionNameEnum) => {
            this._logger.debug(
                `_completeAction: ${this.actionName}: starting completion of the action named: ${actionName}`,
            );

            if (actionName === this.actionName) {
                sub?.unsubscribe();
                executionDO?.resolve();

                this._logger.debug(`_completeAction: ${this.actionName}: action was completed.`);

                return;
            }

            this._logger.debug(
                `_completeAction: ${this.actionName}: action was not completed because of action name mismatch. current action name = ${this.actionName}, received action name: ${actionName}.`,
            );
        };

    private _createCompletionObject = async () => {
        this._actionExecutionDO = new DeferredObject<void>();
        this._postLoginCompletionSubscription =
            this.postLoginChannel.topics.completeAction.subscribe(
                (data: ICompleteActionPostLoginModel) => {
                    this._logger.debug(
                        `createCompletionObject: ${
                            this.actionName
                        }: completeAction subscription: Received message to complete action with the following name: ${
                            data?.actionName || 'undefined'
                        }`,
                    );
                    this._completeAction(
                        this._postLoginCompletionSubscription,
                        this._actionExecutionDO,
                    )(data?.actionName);
                },
            );

        return this._actionExecutionDO.promise;
    };
}
